import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import PricingCalculator from '../components/PricingCalculator';
import { 
  Building2, 
  Dumbbell, 
  GraduationCap, 
  Hospital, 
  Users, 
  HomeIcon,
  TrendingUp,
  Shield,
  BarChart3,
  Package,
  Sparkles,
  Mail,
  Phone,
  MessageCircle,
  CheckCircle,
  ArrowRight,
  Download,
  MapPin,
  IndianRupee
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VendorPartnership = () => {
  const [formData, setFormData] = useState({
    brandName: '',
    contactPerson: '',
    phone: '',
    email: '',
    website: '',
    productCategory: '',
    skuList: '',
    priceRange: '',
    shelfLife: '',
    currentMarkets: '',
    productionCapacity: '',
    preferredLocations: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/vendors/register`, {
        brandName: formData.brandName,
        contactPerson: formData.contactPerson,
        phone: formData.phone,
        email: formData.email,
        website: formData.website,
        productCategory: formData.productCategory,
        skuList: formData.skuList,
        priceRange: formData.priceRange,
        shelfLife: formData.shelfLife,
        currentMarkets: formData.currentMarkets,
        productionCapacity: formData.productionCapacity,
        preferredLocations: formData.preferredLocations,
        message: formData.message
      });

      if (response.data) {
        toast.success('Registration successful! We\'ll contact you within 24 hours.');
        setFormData({
          brandName: '',
          contactPerson: '',
          phone: '',
          email: '',
          website: '',
          productCategory: '',
          skuList: '',
          priceRange: '',
          shelfLife: '',
          currentMarkets: '',
          productionCapacity: '',
          preferredLocations: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Registration failed. Please try again or contact us directly.');
    }
  };

  const locations = [
    { icon: Dumbbell, title: 'Gyms & Fitness Centers', desc: 'Health-conscious consumers' },
    { icon: GraduationCap, title: 'Schools & Institutes', desc: 'Young demographic reach' },
    { icon: Hospital, title: 'Hospitals & Clinics', desc: 'Premium healthcare locations' },
    { icon: Building2, title: 'Corporate Offices', desc: 'Professional workforce' },
    { icon: Users, title: 'Business Centers', desc: 'High-traffic hubs' },
    { icon: HomeIcon, title: 'Residential Societies', desc: 'Daily consumer touchpoint' }
  ];

  const benefits = [
    { icon: TrendingUp, title: 'Direct Consumer Access', desc: 'Reach targeted consumer groups in high-impulse environments' },
    { icon: Shield, title: 'Zero Retail Hassle', desc: 'No onboarding complexity - we handle everything backend' },
    { icon: BarChart3, title: 'Real-time Insights', desc: 'Live sales visibility and SKU-level performance data' },
    { icon: Package, title: 'Full Operations Support', desc: 'Logistics, replenishment, maintenance - all managed' }
  ];

  const faqs = [
    {
      q: 'What is the minimum commitment period?',
      a: 'We require a minimum 3-month commitment with a refundable security deposit. This allows sufficient time to optimize product mix and demonstrate sales performance.'
    },
    {
      q: 'How many SKUs can we place per machine?',
      a: 'Each machine can accommodate 15-25 SKUs depending on product size. We recommend starting with your best-selling products and expanding based on performance data.'
    },
    {
      q: 'Who handles product restocking?',
      a: 'Our team manages the entire logistics and replenishment process. You supply the inventory to our warehouse, and we ensure machines are always stocked.'
    },
    {
      q: 'How do we track sales performance?',
      a: 'You receive detailed monthly reports with SKU-level sales data, location performance, and inventory movement. We also provide recommendations for optimization.'
    },
    {
      q: 'What happens to expired or near-expiry products?',
      a: 'We monitor expiry dates closely and alert you in advance. Brands are responsible for replacing near-expiry stock, and we coordinate the logistics.'
    },
    {
      q: 'Can we choose specific locations for our products?',
      a: 'Yes! While we recommend placements based on your product category, you can specify preferred location types (gyms, offices, etc.) during onboarding.'
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-sage/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_e23403e2-6603-44cf-9c70-7eef2be7d030/artifacts/cv1nky8s_1000050582.png" 
              alt="LiveNeat Logo" 
              className="h-12 w-12 rounded-full object-cover"
            />
            <div>
              <h1 className="text-xl font-bold text-forest">LiveNeat</h1>
              <p className="text-xs text-sage">Vending Network</p>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#locations" className="text-sm font-medium text-forest hover:text-olive transition-colors">Locations</a>
            <a href="#benefits" className="text-sm font-medium text-forest hover:text-olive transition-colors">Benefits</a>
            <a href="#pricing" className="text-sm font-medium text-forest hover:text-olive transition-colors">Pricing</a>
            <a href="#register" className="text-sm font-medium text-forest hover:text-olive transition-colors">Partner With Us</a>
          </div>
          <Button 
            className="bg-olive hover:bg-forest text-white"
            onClick={() => document.getElementById('register').scrollIntoView({ behavior: 'smooth' })}
          >
            Get Started
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sage/10 via-cream to-moss/5 -z-10"></div>
        <div className="absolute top-20 right-10 w-72 h-72 bg-olive/5 rounded-full blur-3xl -z-10"></div>
        <div className="absolute bottom-10 left-10 w-96 h-96 bg-moss/5 rounded-full blur-3xl -z-10"></div>
        
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-olive/10 rounded-full">
                <p className="text-sm font-semibold text-olive">🚀 Vendor Partnership Opportunity</p>
              </div>
              <h1 className="text-5xl md:text-6xl font-bold text-forest leading-tight">
                Scale Your Brand.<br />
                <span className="text-olive">Capture Real Consumers.</span>
              </h1>
              <p className="text-xl text-earth leading-relaxed">
                Join our premium smart vending network across Noida/NCR and grow your offline sales in high-visibility, high-consumption locations.
              </p>
              <div className="flex flex-wrap gap-4 pt-4">
                <Button 
                  size="lg" 
                  className="bg-olive hover:bg-forest text-white group"
                  onClick={() => document.getElementById('register').scrollIntoView({ behavior: 'smooth' })}
                >
                  Become a Partner
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-olive text-olive hover:bg-olive hover:text-white"
                  onClick={() => window.open('https://wa.me/918595672124', '_blank')}
                >
                  <MessageCircle className="mr-2 h-4 w-4" />
                  WhatsApp Us
                </Button>
              </div>
              <div className="flex items-center gap-8 pt-6">
                <div>
                  <p className="text-3xl font-bold text-forest">50+</p>
                  <p className="text-sm text-sage">Premium Locations</p>
                </div>
                <div className="w-px h-12 bg-sage/20"></div>
                <div>
                  <p className="text-3xl font-bold text-forest">25-30%</p>
                  <p className="text-sm text-sage">Revenue Share</p>
                </div>
                <div className="w-px h-12 bg-sage/20"></div>
                <div>
                  <p className="text-3xl font-bold text-forest">100%</p>
                  <p className="text-sm text-sage">Operational Support</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl transform hover:scale-105 transition-transform duration-500">
                <img 
                  src="https://images.unsplash.com/photo-1599762427591-4b754b538b1f" 
                  alt="Modern Vending Machine" 
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-forest/50 to-transparent"></div>
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-lg p-4 border-l-4 border-olive">
                <p className="text-sm text-sage">Placed in</p>
                <p className="text-2xl font-bold text-forest">High-Traffic Zones</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Location Types */}
      <section id="locations" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Strategic High-Potential Locations
            </h2>
            <p className="text-xl text-earth max-w-3xl mx-auto">
              Our vending machines are deployed across premium venues ensuring maximum visibility and repeat consumption
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {locations.map((location, idx) => (
              <Card key={idx} className="border-2 border-sage/20 hover:border-olive hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-14 h-14 bg-olive/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-olive group-hover:scale-110 transition-all duration-300">
                    <location.icon className="h-7 w-7 text-olive group-hover:text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-forest mb-2">{location.title}</h3>
                  <p className="text-sage">{location.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="relative rounded-xl overflow-hidden h-64 group">
              <img 
                src="https://images.unsplash.com/photo-1771586791190-97ed536c54af" 
                alt="Gym Location" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">Premium Gym Facilities</p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-64 group">
              <img 
                src="https://images.unsplash.com/photo-1684831652071-094a540554f0" 
                alt="Office Location" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">Corporate Offices</p>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-64 group">
              <img 
                src="https://images.unsplash.com/photo-1765728617805-b9f22d64e5b3" 
                alt="Fitness Center" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest/80 to-transparent flex items-end p-6">
                <p className="text-white font-semibold text-lg">Fitness Centers</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section id="benefits" className="py-20 px-4 bg-sage/5">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Why Partner With LiveNeat?
            </h2>
            <p className="text-xl text-earth max-w-3xl mx-auto">
              This is not just shelf space — it's a complete retail distribution channel
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {benefits.map((benefit, idx) => (
              <div key={idx} className="flex gap-6 p-6 bg-white rounded-xl border-2 border-sage/10 hover:border-olive hover:shadow-lg transition-all duration-300">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-olive/10 rounded-xl flex items-center justify-center">
                    <benefit.icon className="h-8 w-8 text-olive" />
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-forest mb-2">{benefit.title}</h3>
                  <p className="text-earth leading-relaxed">{benefit.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <Card className="bg-gradient-to-br from-olive/5 to-moss/5 border-2 border-olive/20">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <Sparkles className="h-8 w-8 text-olive flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-2xl font-bold text-forest mb-3">Complete Operational Support</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-olive" />
                      <p className="text-earth">Logistics & Replenishment</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-olive" />
                      <p className="text-earth">Storage & Inventory Management</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-olive" />
                      <p className="text-earth">Machine Operations & Maintenance</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-olive" />
                      <p className="text-earth">Sales Tracking & Reporting</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Transparent Commercial Terms
            </h2>
            <p className="text-xl text-earth">Clear, straightforward pricing with no hidden costs</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-2 border-olive/30 hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-olive/10 rounded-lg flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-olive" />
                  </div>
                  <h3 className="text-2xl font-bold text-forest">Machine Placement Fee</h3>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <IndianRupee className="h-8 w-8 text-olive" />
                    <span className="text-5xl font-bold text-forest">5,000</span>
                    <span className="text-earth">/machine/month</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-olive flex-shrink-0 mt-0.5" />
                    <p className="text-earth">3-month refundable security deposit</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-olive flex-shrink-0 mt-0.5" />
                    <p className="text-earth">Month 1: Paid in advance</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-olive flex-shrink-0 mt-0.5" />
                    <p className="text-earth">From Month 2 onwards: Adjusted from sales</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-olive/30 hover:shadow-2xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-olive/10 rounded-lg flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-olive" />
                  </div>
                  <h3 className="text-2xl font-bold text-forest">Revenue Share</h3>
                </div>
                <div className="mb-6">
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-forest">25-30%</span>
                    <span className="text-earth">on actual sales</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-olive flex-shrink-0 mt-0.5" />
                    <p className="text-earth">Performance-based model</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-olive flex-shrink-0 mt-0.5" />
                    <p className="text-earth">Monthly billing & detailed sales reports</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-olive flex-shrink-0 mt-0.5" />
                    <p className="text-earth">SKU-level performance insights</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section */}
      <section className="py-20 px-4 bg-sage/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Calculate Your Partnership ROI
            </h2>
            <p className="text-xl text-earth max-w-3xl mx-auto">
              Use our interactive calculator to estimate your monthly costs, revenue, and profit potential
            </p>
          </div>
          <PricingCalculator />
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 px-4 bg-sage/5">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Ideal Product Categories
            </h2>
            <p className="text-xl text-earth">Premium products that resonate with health-conscious consumers</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="relative rounded-xl overflow-hidden h-80 group">
              <img 
                src="https://images.unsplash.com/photo-1569420067112-b57b4f024595" 
                alt="Healthy Snacks" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/50 to-transparent flex items-end p-6">
                <div>
                  <p className="text-white font-bold text-xl mb-1">Healthy Snacks</p>
                  <p className="text-sage text-sm">Organic & natural options</p>
                </div>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-80 group">
              <img 
                src="https://images.unsplash.com/photo-1477506350614-fcdc29a3b157" 
                alt="Protein Products" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/50 to-transparent flex items-end p-6">
                <div>
                  <p className="text-white font-bold text-xl mb-1">Protein & Fitness</p>
                  <p className="text-sage text-sm">Performance nutrition</p>
                </div>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-80 group">
              <img 
                src="https://images.pexels.com/photos/4374579/pexels-photo-4374579.jpeg" 
                alt="Beverages" 
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-forest via-forest/50 to-transparent flex items-end p-6">
                <div>
                  <p className="text-white font-bold text-xl mb-1">Functional Beverages</p>
                  <p className="text-sage text-sm">Energy & hydration</p>
                </div>
              </div>
            </div>
            <div className="relative rounded-xl overflow-hidden h-80 group bg-gradient-to-br from-olive/20 to-moss/20 flex items-center justify-center">
              <div className="text-center p-6">
                <Package className="h-16 w-16 text-olive mx-auto mb-4" />
                <p className="text-forest font-bold text-xl mb-2">Ready-to-Eat</p>
                <p className="text-sage">Convenient meal options</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section id="register" className="py-20 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Become a Partner Today
            </h2>
            <p className="text-xl text-earth">Fill out the form below and our team will reach out within 24 hours</p>
          </div>
          <Card className="border-2 border-sage/20">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Details */}
                <div>
                  <h3 className="text-2xl font-bold text-forest mb-4 flex items-center gap-2">
                    <Building2 className="h-6 w-6 text-olive" />
                    Basic Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="brandName" className="text-forest">Brand Name *</Label>
                      <Input
                        id="brandName"
                        name="brandName"
                        value={formData.brandName}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="contactPerson" className="text-forest">Contact Person *</Label>
                      <Input
                        id="contactPerson"
                        name="contactPerson"
                        value={formData.contactPerson}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-forest">Phone Number *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-forest">Email ID *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="website" className="text-forest">Website / Social Media</Label>
                      <Input
                        id="website"
                        name="website"
                        value={formData.website}
                        onChange={handleInputChange}
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                  </div>
                </div>

                {/* Product Details */}
                <div>
                  <h3 className="text-2xl font-bold text-forest mb-4 flex items-center gap-2">
                    <Package className="h-6 w-6 text-olive" />
                    Product Details
                  </h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="productCategory" className="text-forest">Product Category *</Label>
                      <Input
                        id="productCategory"
                        name="productCategory"
                        value={formData.productCategory}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., Healthy Snacks, Beverages"
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="priceRange" className="text-forest">Price Range *</Label>
                      <Input
                        id="priceRange"
                        name="priceRange"
                        value={formData.priceRange}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., ₹20-₹100"
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="skuList" className="text-forest">SKU List *</Label>
                      <Textarea
                        id="skuList"
                        name="skuList"
                        value={formData.skuList}
                        onChange={handleInputChange}
                        required
                        placeholder="List your product SKUs..."
                        className="mt-1 border-sage/30 focus:border-olive"
                        rows={3}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label htmlFor="shelfLife" className="text-forest">Shelf Life / Expiry Details *</Label>
                      <Input
                        id="shelfLife"
                        name="shelfLife"
                        value={formData.shelfLife}
                        onChange={handleInputChange}
                        required
                        placeholder="e.g., 6 months from manufacturing"
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                  </div>
                </div>

                {/* Distribution Readiness */}
                <div>
                  <h3 className="text-2xl font-bold text-forest mb-4 flex items-center gap-2">
                    <TrendingUp className="h-6 w-6 text-olive" />
                    Distribution Readiness
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="currentMarkets" className="text-forest">Current Markets</Label>
                      <Input
                        id="currentMarkets"
                        name="currentMarkets"
                        value={formData.currentMarkets}
                        onChange={handleInputChange}
                        placeholder="e.g., Delhi NCR, Mumbai"
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="productionCapacity" className="text-forest">Monthly Production Capacity</Label>
                      <Input
                        id="productionCapacity"
                        name="productionCapacity"
                        value={formData.productionCapacity}
                        onChange={handleInputChange}
                        placeholder="e.g., 10,000 units/month"
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="preferredLocations" className="text-forest">Preferred Locations (if any)</Label>
                      <Input
                        id="preferredLocations"
                        name="preferredLocations"
                        value={formData.preferredLocations}
                        onChange={handleInputChange}
                        placeholder="e.g., Gyms, Corporate Offices"
                        className="mt-1 border-sage/30 focus:border-olive"
                      />
                    </div>
                    <div>
                      <Label htmlFor="message" className="text-forest">Additional Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Any specific requirements or questions..."
                        className="mt-1 border-sage/30 focus:border-olive"
                        rows={4}
                      />
                    </div>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-olive hover:bg-forest text-white text-lg"
                >
                  Submit Registration
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 px-4 bg-sage/5">
        <div className="container mx-auto max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-forest mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-earth">Everything you need to know about partnering with us</p>
          </div>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, idx) => (
              <AccordionItem 
                key={idx} 
                value={`item-${idx}`} 
                className="bg-white border-2 border-sage/20 rounded-lg px-6 hover:border-olive transition-colors"
              >
                <AccordionTrigger className="text-left text-lg font-semibold text-forest hover:text-olive">
                  {faq.q}
                </AccordionTrigger>
                <AccordionContent className="text-earth leading-relaxed">
                  {faq.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Download Brochure Section */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-gradient-to-br from-olive/10 to-moss/10 border-2 border-olive/20">
            <CardContent className="p-8 text-center">
              <Download className="h-12 w-12 text-olive mx-auto mb-4" />
              <h3 className="text-3xl font-bold text-forest mb-3">Download Partnership Brochure</h3>
              <p className="text-earth mb-6 max-w-2xl mx-auto">
                Get detailed information about our partnership program, location insights, and commercial terms in a comprehensive PDF document.
              </p>
              <Button 
                size="lg" 
                className="bg-olive hover:bg-forest text-white"
                onClick={() => {
                  window.open(`${BACKEND_URL}/api/brochure/download`, '_blank');
                  toast.success('Downloading brochure...');
                }}
              >
                <Download className="mr-2 h-5 w-5" />
                Download Brochure (PDF)
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-20 px-4 bg-yellow-400">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-forest mb-6">
            Ready to Scale Your Brand?
          </h2>
          <p className="text-xl text-olive mb-12 max-w-3xl mx-auto">
            Position your brand where decisions happen — at the point of consumption. Join us and grow your brand where customers actually buy.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="bg-white/90 backdrop-blur-sm border-olive/20">
              <CardContent className="p-6 text-center">
                <Phone className="h-8 w-8 text-olive mx-auto mb-3" />
                <p className="text-sm text-earth mb-2">Call Us</p>
                <a href="tel:+917411515444" className="text-forest font-semibold hover:text-olive transition-colors">
                  +91 7411515444
                </a>
                <br />
                <a href="tel:+918595672124" className="text-forest font-semibold hover:text-olive transition-colors">
                  +91 8595672124
                </a>
              </CardContent>
            </Card>
            <Card className="bg-white/90 backdrop-blur-sm border-olive/20">
              <CardContent className="p-6 text-center">
                <MessageCircle className="h-8 w-8 text-olive mx-auto mb-3" />
                <p className="text-sm text-earth mb-2">WhatsApp</p>
                <a 
                  href="https://wa.me/918595672124" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-forest font-semibold hover:text-olive transition-colors"
                >
                  Chat on WhatsApp
                </a>
              </CardContent>
            </Card>
            <Card className="bg-white/90 backdrop-blur-sm border-olive/20">
              <CardContent className="p-6 text-center">
                <Mail className="h-8 w-8 text-olive mx-auto mb-3" />
                <p className="text-sm text-earth mb-2">Email Us</p>
                <a 
                  href="mailto:awadhudyam.business@gmail.com" 
                  className="text-forest font-semibold hover:text-olive transition-colors text-sm"
                >
                  awadhudyam.business@gmail.com
                </a>
              </CardContent>
            </Card>
          </div>
          <Button 
            size="lg" 
            className="bg-forest text-white hover:bg-olive"
            onClick={() => document.getElementById('register').scrollIntoView({ behavior: 'smooth' })}
          >
            Get Started Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-forest text-white py-12 px-4">
        <div className="container mx-auto max-w-7xl">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e23403e2-6603-44cf-9c70-7eef2be7d030/artifacts/cv1nky8s_1000050582.png" 
                  alt="LiveNeat Logo" 
                  className="h-10 w-10 rounded-full"
                />
                <div>
                  <p className="font-bold text-lg">LiveNeat</p>
                  <p className="text-xs text-sage">Vending Network</p>
                </div>
              </div>
              <p className="text-sm text-sage">
                Building India's smartest vending network for premium brands
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Quick Links</h4>
              <div className="space-y-2 text-sm">
                <a href="#locations" className="block text-sage hover:text-white transition-colors">Locations</a>
                <a href="#benefits" className="block text-sage hover:text-white transition-colors">Benefits</a>
                <a href="#pricing" className="block text-sage hover:text-white transition-colors">Pricing</a>
                <a href="#register" className="block text-sage hover:text-white transition-colors">Register</a>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Contact</h4>
              <div className="space-y-2 text-sm text-sage">
                <p>+91 8595672124</p>
                <p>+91 7411515444</p>
                <p>awadhudyam.business@gmail.com</p>
                <p>liveneat.organic@gmail.com</p>
              </div>
            </div>
            <div>
              <h4 className="font-semibold mb-3">Location Coverage</h4>
              <p className="text-sm text-sage">Noida / NCR Region</p>
              <p className="text-xs text-sage mt-2">Expanding across North India</p>
            </div>
          </div>
          <div className="border-t border-sage/20 pt-6 text-center text-sm text-sage">
            <p>&copy; 2024 LiveNeat Vending Network. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VendorPartnership;
